import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import PoolIcon from "@mui/icons-material/Pool";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsIcon from "@mui/icons-material/Sports";
import { collectWeeklyReportActivities } from "../../../../../utils/weeklyReportActivities";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const ActivityRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ACTIVITY_ICONS = {
  Running: <DirectionsRunIcon fontSize="small" color="primary" />,
  Walking: <DirectionsWalkIcon fontSize="small" color="primary" />,
  Cycling: <DirectionsBikeIcon fontSize="small" color="primary" />,
  Swimming: <PoolIcon fontSize="small" color="primary" />,
  "Traditional Strength Training": <FitnessCenterIcon fontSize="small" color="primary" />,
  "Functional Strength Training": <FitnessCenterIcon fontSize="small" color="primary" />,
  "High Intensity Interval Training": <FitnessCenterIcon fontSize="small" color="primary" />,
  CrossTraining: <FitnessCenterIcon fontSize="small" color="primary" />,
  default: <SportsIcon fontSize="small" color="primary" />,
};

function getActivityIcon(activityName) {
  return ACTIVITY_ICONS[activityName] || ACTIVITY_ICONS.default;
}

function formatDuration(minutes) {
  if (!minutes || minutes === 0) return null;
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDistance(meters) {
  if (!meters || meters === 0) return null;
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${meters} m`;
}

const ActivitiesCard = ({ report }) => {
  const activities = React.useMemo(
    () => collectWeeklyReportActivities(report),
    [report],
  );

  if (activities.length === 0) {
    return null;
  }

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <DirectionsRunIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Health Activities
            </Typography>
            <Chip
              label={activities.length}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
        }
        subheader="Fitness activities synced from Apple Health"
      />
      <CardContentWrapper>
        <Stack spacing={1.5}>
          {activities.map((activity, index) => {
            const duration = formatDuration(activity.durationMinutes);
            const distance = formatDistance(activity.distanceMeters);
            const calories = activity.activeCalories > 0 ? `${activity.activeCalories} kcal` : null;
            const dateLabel = activity.dateIndex
              ? activity.dateIndex
              : activity.startDate
                ? new Date(activity.startDate).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                : null;

            return (
              <ActivityRow key={`${activity.dateIndex || "day"}-${activity.activityId || index}-${index}`}>
                {getActivityIcon(activity.activityName)}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight="medium" noWrap>
                    {activity.activityName}
                  </Typography>
                  {dateLabel && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      sx={{ display: "block" }}
                    >
                      {dateLabel}
                    </Typography>
                  )}
                  {activity.sourceApp && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      sx={{ display: "block" }}
                    >
                      via {activity.sourceApp}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                  {duration && (
                    <Chip label={duration} size="small" variant="outlined" />
                  )}
                  {distance && (
                    <Chip label={distance} size="small" variant="outlined" />
                  )}
                  {calories && (
                    <Chip label={calories} size="small" variant="outlined" color="warning" />
                  )}
                </Stack>
              </ActivityRow>
            );
          })}
        </Stack>
      </CardContentWrapper>
    </StyledCard>
  );
};

export default ActivitiesCard;
